import AiWriteButton from "../components/AiWriteButton";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel,
  Avatar,
  Chip,
  InputAdornment,
  RadioGroup,
  Radio,
  IconButton,
} from "@mui/material";
import { getDatabase, ref, onValue, set } from "firebase/database";
import EventDatesField from "../components/EventDatesField";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { VideoLibrary as VideoLibraryIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { auth, db, uploadImage, uploadVideo } from "../firebase";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import BackButton from "./BackButton";
import { useSubscriptionContext } from "./SubscriptionContext";
import { T } from "./Translate";
import {
  HORARIO_OPTIONS,
  geocodeAddress,
  searchAddressSuggestions,
  searchUsersByName,
  EVENT_COMMISSION_RATE,
  rowsToEventDates,
  normalizeEventUrl,
} from "./eventsUtils";
import { getCountryFromIP } from "./homeUtils";
import { notifyFollowers } from "../utils/notifications";
import { pageContentSx } from "../pageLayout";
import { barColors } from "../theme";
import { uploadButtonSx, uploadProgressSx, mediaPreviewStyle, mediaPreviewBoxSx, removeMediaButtonSx, removeMediaIconSx } from "./mediaFormStyles";

const containsOffensiveLanguage = (text) => {
  // Basic list of offensive words - expand as needed
  const offensiveWords = [
    "pija",
    "puto",
    "hijo de puta",
    "travesti",
    "trolo",
    "puta",
    "pijo",
    "hijo de puto",
    "gay",
    "hijo de re mil",
    "hijo de re mil puta",
    "chupa pija",
    "trolazo",
    "pene",
    "pito",
  ];

    // Convert to lowercase for case-insensitive matching
    const lowerText = text.toLowerCase();
  
    // Check for exact matches and partial matches
    return offensiveWords.some(word => 
      lowerText.includes(word.toLowerCase()) ||
      // Check for common letter substitutions
      lowerText.replace(/[4@]/g, 'a')
               .replace(/[3]/g, 'e')
               .replace(/[1!]/g, 'i')
               .replace(/[0]/g, 'o')
               .includes(word.toLowerCase())
    );
  };

const PublishEvent = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dateRows: [{ day: null, time: "" }],
    direccion: "",
    horario: "",
    images: [],
    videos: [],
    eventType: "", // Nuevo campo para el tipo de evento
    url: "",
    cupon: "",
  });
  const [loading, setLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  // Cuando está activo, el evento se publica sin el campo "country" para que
  // filterOffersByCountry (homeUtils.js) lo muestre a usuarios de cualquier país,
  // en vez de solo a los del país detectado por IP al momento de publicar.
  const [worldwide, setWorldwide] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const { subscription, isSubscriptionActive } = useSubscriptionContext(); // Correct usage

  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimeout = useRef(null);
  const selectedAddressCoordsRef = useRef(null);

  // Visibilidad: evento público (lo ve cualquiera) o privado (solo el
  // organizador y los participantes elegidos, ver firestore.rules).
  const [visibility, setVisibility] = useState("publico");
  const [participants, setParticipants] = useState([]);
  const [participantOptions, setParticipantOptions] = useState([]);
  const [participantSearchLoading, setParticipantSearchLoading] = useState(false);
  const [participantInput, setParticipantInput] = useState("");
  const participantSearchTimeout = useRef(null);

  // Evento pago: la entrada se cobra vía PayPal directo al organizador
  // (paypalEmail), Notiline factura la comisión aparte (ver EVENT_COMMISSION_RATE
  // en eventsUtils.js y el flujo de compra en events.js), igual que las tiendas.
  const [isPaid, setIsPaid] = useState(false);
  const [ticketPrice, setTicketPrice] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");

  const handleParticipantInputChange = (event, newInputValue, reason) => {
    setParticipantInput(newInputValue);

    if (reason !== "input") return;

    if (participantSearchTimeout.current) {
      clearTimeout(participantSearchTimeout.current);
    }

    if (newInputValue.trim().length < 2) {
      setParticipantOptions([]);
      return;
    }

    participantSearchTimeout.current = setTimeout(async () => {
      setParticipantSearchLoading(true);
      const results = await searchUsersByName(newInputValue, auth.currentUser?.uid);
      setParticipantOptions(results);
      setParticipantSearchLoading(false);
    }, 400);
  };

  const handleAddressInputChange = (event, newInputValue, reason) => {
    setFormData((prev) => ({ ...prev, direccion: newInputValue }));
    setErrors((prev) => ({ ...prev, direccion: "" }));

    if (reason !== "input") return;

    selectedAddressCoordsRef.current = null;

    if (addressSearchTimeout.current) {
      clearTimeout(addressSearchTimeout.current);
    }

    if (newInputValue.trim().length < 3) {
      setAddressOptions([]);
      return;
    }

    addressSearchTimeout.current = setTimeout(async () => {
      setAddressLoading(true);
      const results = await searchAddressSuggestions(newInputValue);
      setAddressOptions(results);
      setAddressLoading(false);
    }, 400);
  };

  const handleAddressSelect = (event, newValue) => {
    if (newValue && typeof newValue === "object") {
      setFormData((prev) => ({ ...prev, direccion: newValue.label }));
      selectedAddressCoordsRef.current = {
        latitude: newValue.latitude,
        longitude: newValue.longitude,
      };
    }
  };

  useEffect(() => {
    if (!subscription) {
      navigate('/subscription');
    }
  }, [subscription, navigate]);

  const PLAN_LIMITS_EVENTS = { Free: 2, Básico: 2, Profesional: 5, Premium: Infinity };
  const [eventCount, setEventCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setEventCount(snap.exists() ? snap.val().events || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const eventLimit = PLAN_LIMITS_EVENTS[subscription?.planName] ?? 0;
  const canPublishEvent = isSubscriptionActive && eventCount <= eventLimit;
  const eventLimitMsg = eventCount > eventLimit
    ? <T>Has superado el límite de {eventLimit} evento{eventLimit !== 1 ? "s" : ""} por mes de tu plan {subscription?.planName}. Mejora tu plan para publicar más.</T>
    : "";

  // Eventos destacados: mismo beneficio que las ofertas destacadas
  // (Profesional/Premium), sube el evento al principio del listado (ver
  // sort por "featured" en events.js). Básico no tiene cupo.
  const FEATURED_LIMITS_EVENTS = { Básico: 0, Profesional: 1, Premium: 3 };
  const [featured, setFeatured] = useState(false);
  const [featuredCount, setFeaturedCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setFeaturedCount(snap.exists() ? snap.val().featuredEvents || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const featuredLimit = FEATURED_LIMITS_EVENTS[subscription?.planName] ?? 0;
  const canFeatureEvent = featuredLimit > 0 && featuredCount < featuredLimit;

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    const requiredStringFields = ['title', 'description', 'eventType'];
    requiredStringFields.forEach(key => {
        if (!formData[key] || formData[key].trim() === "") {
            newErrors[key] = <T>Este campo es obligatorio</T>;
            isValid = false;
        }
    });

    if (rowsToEventDates(formData.dateRows).length === 0) {
        newErrors.date = <T>Elegí al menos una fecha</T>;
        isValid = false;
    }

    // Check for offensive language in text fields
    const textFields = ['title', 'description', 'cupon'];
    textFields.forEach(field => {
      if (formData[field] && typeof formData[field] === 'string' && containsOffensiveLanguage(formData[field])) {
        newErrors[field] = <T>Por favor, evite usar lenguaje inapropiado</T>;
        isValid = false;
      }
    });

    // Existing media validation
    if (formData.images.length === 0 && formData.videos.length === 0) {
      newErrors.media = <T>Debes subir al menos una imagen o un video</T>;
      isValid = false;
    }

    if (normalizeEventUrl(formData.url) === null) {
      newErrors.url = <T>Ingresá una URL válida (por ejemplo, https://misitio.com)</T>;
      isValid = false;
    }

    if (visibility === "privado" && participants.length === 0) {
      newErrors.participants = <T>Elegí al menos un participante para un evento privado</T>;
      isValid = false;
    }

    if (isPaid) {
      const price = parseFloat(ticketPrice);
      if (!ticketPrice || isNaN(price) || price <= 0) {
        newErrors.ticketPrice = <T>Ingresá un precio válido para la entrada</T>;
        isValid = false;
      } else if (price > 999999.99) {
        newErrors.ticketPrice = <T>El precio no puede exceder 999,999.99</T>;
        isValid = false;
      }

      // PayPal es opcional: sin correo, la entrada se compra en el enlace o en puerta.
      const cleanPaypalEmail = paypalEmail.trim();
      if (cleanPaypalEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanPaypalEmail)) {
        newErrors.paypalEmail = <T>Ingresá un correo de PayPal válido</T>;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Check for offensive language as user types
    if (['product', 'description', 'cupon'].includes(name) && containsOffensiveLanguage(value)) {
      setErrors(prev => ({
        ...prev,
        [name]: <T>Por favor, evite usar lenguaje inapropiado</T>
      }));
    } else {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  

  const handlePublishEvents = async () => {
    if (!validateForm()) {
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      console.error("No user is signed in");
      return;
    }

    setLoading(true);

    try {
      const { uid } = user;
      const userDocRef = doc(db, "users", uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (userDocSnapshot.exists()) {
        const userData = userDocSnapshot.data();
        // uid siempre viene de auth, no de userData: el doc de perfil no
        // necesariamente guarda su propio uid como campo (es el id del doc).
        const userId = uid;
        const { displayName: userName, profileImageUrl: imageProfile } = userData;
        const country = worldwide ? null : await getCountryFromIP();
        const coordinates =
          selectedAddressCoordsRef.current || (await geocodeAddress(formData.direccion));
        const isFeatured = featured && canFeatureEvent;
        const eventDates = rowsToEventDates(formData.dateRows);

        const eventDocRef = await addDoc(collection(db, "events"), {
          uid: userId,
          name: userName,
          image: imageProfile,
          title: formData.title,
          description: formData.description,
          // `date` = primera fecha, para las pantallas que leen una sola (ver eventsUtils).
          date: eventDates[0],
          dates: eventDates,
          direccion: formData.direccion,
          horario: formData.horario,
          latitude: coordinates?.latitude ?? null,
          longitude: coordinates?.longitude ?? null,
          images: formData.images,
          videos: formData.videos,
          eventType: formData.eventType, // Guardar el tipo de evento
          url: normalizeEventUrl(formData.url),
          cupon: formData.url.trim() ? formData.cupon.trim() : "",
          country,
          isPrivate: visibility === "privado",
          participantIds:
            visibility === "privado" ? [userId, ...participants.map((p) => p.uid)] : [],
          isPaid,
          ticketPrice: isPaid ? parseFloat(ticketPrice) : null,
          paypalEmail: isPaid && paypalEmail.trim() ? paypalEmail.trim() : null,
          ticketBuyers: [],
          featured: isFeatured,
          createdAt: serverTimestamp(),
        });

        if (isFeatured) {
          const rtdb = getDatabase();
          const now = new Date();
          const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
          await set(ref(rtdb, `publicationCounts/${userId}/${monthYear}/featuredEvents`), featuredCount + 1);
        }

        if (visibility !== "privado") {
          notifyFollowers({
            ownerUid: userId,
            category: "events",
            type: "event",
            message: `${userName} publicó un nuevo evento: ${formData.title}`,
            postId: eventDocRef.id,
          });
        }

        setPublishSuccess(true);
      } else {
        console.error("User document does not exist");
        setErrors({ general: <T>Error al obtener datos del usuario</T> });
      }
    } catch (error) {
      console.error("Error publishing event:", error);
      setErrors({ general: <T>Error al publicar el evento</T> });
    } finally {
      setLoading(false);
    }
  };

  const handleDateRowsChange = (dateRows) => {
    setFormData((prev) => ({ ...prev, dateRows }));
    setErrors((prevErrors) => ({ ...prevErrors, date: "" }));
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;

    if (!files) return;

    setLoading(true);

    try {
      const imageUrls = [];

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, ...imageUrls],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const files = event.target.files;

    if (!files) return;

    setLoading(true);

    try {
      const videoUrls = [];

      for (const file of files) {
        const videoUrl = await uploadVideo(file);
        videoUrls.push(videoUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        videos: [...prevFormData.videos, ...videoUrls],
      }));
    } catch (error) {
      console.error("Error uploading videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMedia = (type, index) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [type]: prevFormData[type].filter((_, i) => i !== index),
    }));
  };

  const handleRedirectHome = () => {
    navigate("/events");
  };

  const styles = {
    appBar: {
      bgcolor: "#312783",
    },
    toolbar: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      padding: "10px",
    },
    container: {
      mt: 14,
    },
    uploadButton: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "50px",
      backgroundColor: "#2a2a2a",
      border: "2px solid #a0b4ff",
      marginTop: "20px",
      borderRadius: "8px",
      color: "white",
    },
    progress: {
      position: "absolute",
      color: "#a0b4ff",
    },
    publishButton: {
      mt: 3,
    },
    errorText: {
      color: "red",
      fontSize: "0.75rem",
      marginTop: "3px",
    },
    imagePreview: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      margin: "5px",
      border: "1px solid rgba(255,255,255,0.2)",
      borderRadius: "5px",
    },
    removeButton: {
      display: "block",
      color: "white",
      cursor: "pointer",
    },
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
        <Toolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              maxWidth: { xs: "100%", md: "980px" },
              mx: "auto",
              px: 2,
              boxSizing: "border-box",
            }}
          >
            <BackButton color="inherit" />
            <Typography variant="h5" noWrap>
              <T>Publicar Evento</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 15, mb: 20 }}>
        <TextField
          autoFocus
          margin="dense"
          id="title"
          label={<T>Título del evento</T>}
          type="text"
          fullWidth
          required
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          error={!!errors.title}
          helperText={errors.title}
        />
        <TextField
          margin="dense"
          id="description"
          label={<T>Descripción del evento</T>}
          type="text"
          fullWidth
          required
          name="description"
          rows={4}
          inputProps={{
            maxLength: 350
          }}
          multiline
          value={formData.description}
          onChange={handleInputChange}
          error={!!errors.description}
          helperText={errors.description}
        />
        <AiWriteButton
          type="event"
          maxDescription={350}
          fields={{
            titulo: formData.title,
            descripcion: formData.description,
            tipo: formData.eventType,
            direccion: formData.direccion,
            horario: formData.horario,
            entrada: isPaid ? `paga, $${ticketPrice}` : "",
          }}
          onResult={({ title, description }) =>
            setFormData((prev) => ({ ...prev, title: title || prev.title, description: description || prev.description }))
          }
        />
        <EventDatesField value={formData.dateRows} onChange={handleDateRowsChange} error={errors.date} />

        <Autocomplete
          freeSolo
          fullWidth
          filterOptions={(options) => options}
          options={addressOptions}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          getOptionKey={(option) =>
            typeof option === "string" ? option : `${option.label}-${option.latitude}-${option.longitude}`
          }
          loading={addressLoading}
          inputValue={formData.direccion}
          onInputChange={handleAddressInputChange}
          onChange={handleAddressSelect}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              id="direccion"
              label={<T>Dirección del evento</T>}
              placeholder="Empezá a escribir una dirección..."
              fullWidth
              error={!!errors.direccion}
              helperText={errors.direccion}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {addressLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          margin="dense"
          id="url"
          label={<T>Enlace del evento (opcional)</T>}
          placeholder="https://..."
          type="url"
          fullWidth
          name="url"
          value={formData.url}
          onChange={handleInputChange}
          error={!!errors.url}
          helperText={errors.url || <T>Web, venta de entradas o transmisión.</T>}
        />
        {/* El cupón se usa en la página del enlace (venta de entradas
            externa), no en el cobro por PayPal: sin enlace no hay dónde usarlo. */}
        {formData.url.trim() && (
        <TextField
          margin="dense"
          id="cupon"
          label={<T>Cupón de descuento (opcional)</T>}
          type="text"
          fullWidth
          name="cupon"
          inputProps={{ maxLength: 40 }}
          value={formData.cupon}
          onChange={handleInputChange}
          error={!!errors.cupon}
          helperText={errors.cupon || <T>Código de descuento para usar en la página del enlace.</T>}
        />
        )}
        <FormControl fullWidth margin="dense">
          <InputLabel id="horario-label"><T>Horario del evento</T></InputLabel>
          <Select
            labelId="horario-label"
            id="horario"
            name="horario"
            label={<T>Horario del evento</T>}
            value={formData.horario}
            onChange={handleInputChange}
            error={!!errors.horario}
          >
            {HORARIO_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <T>{option.label}</T>
              </MenuItem>
            ))}
          </Select>
          {errors.horario && (
            <Typography sx={styles.errorText}>{errors.horario}</Typography>
          )}
        </FormControl>

        <Typography><T>Tipo de evento:</T></Typography>
        <FormControl fullWidth margin="dense">
          <InputLabel id="eventType-label"><T>Tipo de evento</T></InputLabel>
          <Select
            labelId="eventType-label"
            id="eventType"
            name="eventType"
            label={<T>Tipo de evento</T>}
            value={formData.eventType}
            onChange={handleInputChange}
            error={!!errors.eventType}
          >
            <MenuItem value="exposicion"><T>Exposición</T></MenuItem>
            <MenuItem value="innauguracion"><T>Innauguración</T></MenuItem>
            <MenuItem value="feriado"><T>Feriado</T></MenuItem>
            <MenuItem value="concierto"><T>Concierto</T></MenuItem>
            <MenuItem value="festival"><T>Festival</T></MenuItem>
            <MenuItem value="obraDeTeatro"><T>Obra de teatro</T></MenuItem>
            <MenuItem value="conferencia"><T>Conferencia</T></MenuItem>
            <MenuItem value="otro"><T>Otro</T></MenuItem>
          </Select>
          {errors.eventType && (
            <Typography sx={styles.errorText}>{errors.eventType}</Typography>
          )}
        </FormControl>

        <FormControlLabel
          sx={{ mt: 1, display: "flex" }}
          control={
            <Switch
              checked={worldwide}
              onChange={(e) => setWorldwide(e.target.checked)}
            />
          }
          label={<T>Visible en todos los países (ignora tu ubicación actual)</T>}
        />

        {featuredLimit > 0 && (
          <>
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={featured}
                  onChange={(e) => setFeatured(e.target.checked)}
                  disabled={!canFeatureEvent}
                />
              }
              label={<T>Destacar este evento (aparece primero en el listado)</T>}
            />
            <Typography variant="caption" color={featuredCount >= featuredLimit ? "error" : "text.secondary"}>
              <T>Destacados este mes: {featuredCount} / {featuredLimit}</T>
            </Typography>
          </>
        )}

        <Box mt={3} mb={1}>
          <Typography><T>Visibilidad del evento:</T></Typography>
          <RadioGroup
            row
            name="visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
          >
            <FormControlLabel value="publico" control={<Radio />} label={<T>Público</T>} />
            <FormControlLabel value="privado" control={<Radio />} label={<T>Privado</T>} />
          </RadioGroup>
        </Box>

        {visibility === "privado" && (
          <Box mb={2}>
            <Autocomplete
              multiple
              fullWidth
              filterSelectedOptions
              options={participantOptions}
              getOptionLabel={(option) => option.displayName || ""}
              isOptionEqualToValue={(option, value) => option.uid === value.uid}
              inputValue={participantInput}
              value={participants}
              loading={participantSearchLoading}
              onInputChange={handleParticipantInputChange}
              onChange={(event, newValue) => {
                setParticipants(newValue);
                setErrors((prev) => ({ ...prev, participants: "" }));
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.uid} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar src={option.profileImageUrl} sx={{ width: 24, height: 24 }} />
                  {option.displayName}
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    avatar={<Avatar src={option.profileImageUrl} />}
                    label={option.displayName}
                    {...getTagProps({ index })}
                    key={option.uid}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  label={<T>Participantes</T>}
                  placeholder="Buscar por nombre..."
                  fullWidth
                  error={!!errors.participants}
                  helperText={errors.participants}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {participantSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        <FormControlLabel
          sx={{ mt: 1, display: "flex" }}
          control={
            <Switch
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
            />
          }
          label={<T>Evento pago</T>}
        />

        {isPaid && (
          <Box mb={2}>
            <TextField
              margin="dense"
              label={<T>Precio de la entrada (USD)</T>}
              type="text"
              fullWidth
              required
              value={ticketPrice}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d*$/.test(value)) {
                  setTicketPrice(value);
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value) {
                  const numberValue = parseFloat(value);
                  if (!isNaN(numberValue)) {
                    setTicketPrice(numberValue.toFixed(2));
                  }
                }
              }}
              error={!!errors.ticketPrice}
              helperText={errors.ticketPrice}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <TextField
              margin="dense"
              label={<T>Tu correo de PayPal (opcional)</T>}
              type="email"
              fullWidth
              value={paypalEmail}
              onChange={(e) => {
                setPaypalEmail(e.target.value);
                setErrors((prev) => ({ ...prev, paypalEmail: "" }));
              }}
              error={!!errors.paypalEmail}
              helperText={
                errors.paypalEmail ||
                (formData.url.trim()
                  ? <T>Para cobrar las entradas también con PayPal desde Notiline. Si no, se compran en el enlace del evento.</T>
                  : <T>Para cobrar las entradas con PayPal desde Notiline. Si no, el precio se muestra como referencia.</T>)
              }
            />
            {paypalEmail.trim() && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <T>Notiline cobra {EVENT_COMMISSION_RATE * 100}% de comisión de referencia sobre cada entrada vendida por PayPal, que se factura aparte.</T>
              </Alert>
            )}
          </Box>
        )}

        <Typography><T>Subir Imagenes del evento:</T></Typography>
        <label htmlFor="upload-image-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div"
            sx={uploadButtonSx(!!errors.media)}
            disabled={loading}
          >
            <AddPhotoAlternateIcon />
            <T>Subir Imágenes del producto</T>
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>
        <input
          type="file"
          accept="image/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleImageUpload}
          style={{ display: "none" }}
          id="upload-image-button"
          multiple
        />
        <Typography sx={{ mt: 2 }}><T>Subir Video del evento:</T></Typography>
        <label htmlFor="upload-video-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div"
            sx={uploadButtonSx(!!errors.media)}
            disabled={loading}
          >
            <VideoLibraryIcon />
            <T>Subir Video del producto</T>
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>
        <input
          type="file"
          accept="video/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleVideoUpload}
          style={{ display: "none" }}
          id="upload-video-button"
          multiple
        />

        {errors.media && (
          <Typography color="error" variant="caption">
            {errors.media}
          </Typography>
        )}

        <Grid container sx={{ mt: 2 }}>
          {formData.images.map((imageUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
              <Box sx={mediaPreviewBoxSx(false)}>
                <img
                  src={imageUrl}
                  alt="Imagen del producto"
                  style={mediaPreviewStyle}
                />
                <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveMedia("images", index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
              </Box>
            </Grid>
          ))}
          {formData.videos.map((videoUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
              <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                <video
                  src={videoUrl}
                  controls
                  style={{ width: "100%", borderRadius: "10px" }}
                />
                <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveMedia("videos", index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
        {publishSuccess && (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography><T>El evento se ha publicado correctamente.</T></Typography>
            <Button onClick={handleRedirectHome} color="primary">
              <T>Ir a Eventos</T>
            </Button>
          </Box>
        )}
        {!publishSuccess && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <Typography variant="caption" color={eventCount >= eventLimit ? "error" : "text.secondary"}>
                <T>Eventos este mes: {eventCount} / {eventLimit === Infinity ? "∞" : eventLimit}</T>
              </Typography>
            </Box>
            {eventLimitMsg && (
              <Alert severity="warning" sx={{ mt: 1 }} action={
                <Button size="small" color="inherit" onClick={() => navigate("/subscription")}><T>Ver planes</T></Button>
              }>{eventLimitMsg}</Alert>
            )}
            <Button
              variant="contained"
              sx={styles.publishButton}
              fullWidth
              onClick={handlePublishEvents}
              color="primary"
              disabled={loading || !canPublishEvent}
            >
              {loading ? <T>Publicando...</T> : <T>Publicar</T>}
            </Button>
          </>
        )}
      </Container>
    </Container>
  );
};

export default PublishEvent;
